const BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

/** Shape of a structured JSON error body from the backend. `error` is the
 *  human-readable message every route returns; extra fields (e.g. `refs` on
 *  the template-publish guard) ride along untyped for callers that know them. */
export interface ApiErrorBody {
  error?: string;
  [key: string]: unknown;
}

/** Thrown for any non-OK response. Carries the HTTP status and the parsed
 *  body (object when the backend returned JSON, raw string otherwise) so the
 *  UI can render a real message + structured detail instead of a raw blob. */
export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody | string;

  constructor(status: number, body: ApiErrorBody | string) {
    const message =
      typeof body === 'string'
        ? body || `Request failed (${status})`
        : body.error ?? `Request failed (${status})`;
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/** Build an ApiError from a failed Response, parsing the body as JSON when
 *  possible so structured fields survive. */
async function toApiError(res: Response): Promise<ApiError> {
  const text = await res.text();
  try {
    return new ApiError(res.status, JSON.parse(text) as ApiErrorBody);
  } catch {
    return new ApiError(res.status, text);
  }
}

function buildInit(init: RequestInit | undefined, body: BodyInit | null | undefined): RequestInit {
  const headers: Record<string, string> = { ...(init?.headers as Record<string, string>) };
  // Only set JSON content-type when we're actually sending a body AND it isn't
  // FormData. Sending `Content-Type: application/json` with no body makes
  // Fastify 5 return FST_ERR_CTP_EMPTY_JSON_BODY; sending it with FormData
  // stomps the multipart boundary the browser would otherwise generate.
  if (body != null && !(body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  return {
    ...init,
    credentials: 'include',
    headers,
    body: body ?? undefined,
  };
}

async function request<T>(path: string, init?: RequestInit, rawBody?: BodyInit | null): Promise<T> {
  const res = await fetch(`${BASE}${path}`, buildInit(init, rawBody));

  if (res.status === 401 && path !== '/api/auth/refresh') {
    const refreshed = await fetch(`${BASE}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (refreshed.ok) {
      const retry = await fetch(`${BASE}${path}`, buildInit(init, rawBody));
      if (!retry.ok) throw await toApiError(retry);
      return retry.json();
    }
    throw new ApiError(401, 'Unauthorized');
  }

  if (!res.ok) throw await toApiError(res);
  return res.json();
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(
      path,
      { method: 'POST' },
      body !== undefined ? JSON.stringify(body) : undefined,
    ),
  put: <T>(path: string, body?: unknown) =>
    request<T>(
      path,
      { method: 'PUT' },
      body !== undefined ? JSON.stringify(body) : undefined,
    ),
  postForm: <T>(path: string, form: FormData) =>
    request<T>(path, { method: 'POST' }, form),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
